function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/* DECOALIVE · capa de conversión y coherencia · 2026-08-31 */
(() => {
  const PATH = window.location.pathname.replace(/\/$/, '') || '/';
  const IS_PT = document.documentElement.lang.toLowerCase().startsWith('pt') || PATH === '/pt' || PATH.startsWith('/pt/');
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  function replaceTag(element, tagName) {
    if (!element || element.tagName.toLowerCase() === tagName.toLowerCase()) return element;
    const replacement = document.createElement(tagName);
    for (const attribute of element.attributes) replacement.setAttribute(attribute.name, attribute.value);
    while (element.firstChild) replacement.appendChild(element.firstChild);
    element.replaceWith(replacement);
    return replacement;
  }

  function exactText(root, selector, text) {
    return qsa(selector, root).find((element) => element.textContent.trim() === text);
  }

  function replaceVisibleText(root, replacements) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      if (!node.parentElement || /^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT)$/.test(node.parentElement.tagName)) continue;
      const original = node.nodeValue;
      let updated = original;
      for (const [from, to] of replacements) updated = updated.split(from).join(to);
      if (updated !== original) node.nodeValue = updated;
    }
  }

  function ensureNavigationLinks() { return;
    const links = IS_PT
      ? [
          ['/pt/collections/plantas-de-exterior', 'PLANTAS DE EXTERIOR'],
          ['/pt/blogs/plantas', 'GUIAS E CUIDADOS'],
        ]
      : [
          ['/collections/plantas-de-exterior', 'PLANTAS DE EXTERIOR'],
          ['/blogs/plantas', 'GUÍAS Y CUIDADOS'],
        ];
    const menus = qsa('.header__inline-menu .list-menu, .menu-drawer__menu');
    for (const menu of menus) {
      for (const [href, label] of links) {
        if (qs(`[data-decoalive-nav="${href}"]`, menu)) continue;
        const item = document.createElement('li');
        item.className = 'decoalive-nav-item';
        const link = document.createElement('a');
        link.href = href;
        link.textContent = label;
        link.dataset.decoaliveNav = href;
        link.className = menu.matches('.menu-drawer__menu')
          ? 'menu-drawer__menu-item list-menu__item link link--text focus-inset'
          : 'header__menu-item list-menu__item link link--text focus-inset';
        item.appendChild(link);
        menu.appendChild(item);
      }
    }
  }

  function improveHome() {
    if (PATH !== '/' && PATH !== '/pt') return;
    if (!IS_PT) {
      const hero = exactText(document, 'h1,h2,h3', 'La planta perfecta para tu casa sí existe');
      const lower = exactText(document, 'h1,h2,h3', 'Comprar plantas online en España y Portugal');
      if (hero) replaceTag(hero, 'h1').classList.add('decoalive-primary-h1');
      if (lower) replaceTag(lower, 'h2');
    }
    if (IS_PT) {
      replaceVisibleText(document.body, [
        ['La planta perfecta para tu casa sí existe', 'A planta perfeita para a sua casa existe'],
        ['Te ayudamos a elegir según la luz, tu espacio y tu experiencia. Recíbela protegida en 24–48 h, con envío gratis.', 'Ajudamos a escolher segundo a luz, o espaço e a sua experiência. Receba-a protegida em 24–48 h, com envio grátis.'],
        ['VER MÁS VENDIDAS', 'VER MAIS VENDIDAS'],
        ['opiniones verificadas', 'opiniões verificadas'],
        ['Plantas seleccionadas', 'Plantas selecionadas'],
        ['Preparadas una a una', 'Preparadas uma a uma'],
        ['Embalaje protector', 'Embalagem protetora'],
        ['Viajan seguras hasta tu casa', 'Viajam em segurança até sua casa'],
        ['Compra sin riesgo', 'Compra sem risco'],
        ['Envío y devolución gratis', 'Envio e devolução grátis'],
        ['Las más elegidas por quienes quieren acertar a la primera.', 'As mais escolhidas por quem quer acertar à primeira.'],
        ['ENCUENTRA TU PLANTA IDEAL', 'ENCONTRE A SUA PLANTA IDEAL'],
        ['Empieza por lo que necesitas', 'Comece pelo que precisa'],
        ['Colecciones pensadas para que aciertes a la primera, según tu hogar, tu ritmo y tu experiencia.', 'Coleções pensadas para acertar à primeira, segundo a sua casa, o seu ritmo e a sua experiência.'],
        ['Para hogares con mascotas', 'Para casas com animais'],
        ['Plantas bonitas para convivir con perros y gatos.', 'Plantas bonitas para conviver com cães e gatos.'],
        ['Fáciles y resistentes', 'Fáceis e resistentes'],
        ['Acierta a la primera, incluso con poca experiencia.', 'Acerte à primeira, mesmo com pouca experiência.'],
        ['POCA LUZ', 'POUCA LUZ'],
        ['Para rincones con poca luz', 'Para espaços com pouca luz'],
        ['Verde y estilo para las zonas menos luminosas.', 'Verde e estilo para as zonas menos luminosas.'],
        ['POCA AGUA', 'POUCA ÁGUA'],
        ['Para quienes riegan poco', 'Para quem rega pouco'],
        ['Opciones agradecidas para un ritmo de vida activo.', 'Opções resistentes para um ritmo de vida ativo.'],
        ['GRAN FORMATO', 'GRANDE FORMATO'],
        ['Impacto inmediato', 'Impacto imediato'],
        ['Plantas grandes que transforman cualquier estancia.', 'Plantas grandes que transformam qualquer espaço.'],
        ['MEJOR VALOR', 'MELHOR VALOR'],
        ['Packs ahorro', 'Packs de poupança'],
        ['Más verde, una selección coordinada y mejor precio.', 'Mais verde, uma seleção coordenada e melhor preço.'],
        ['Ver colección', 'Ver coleção'],
        ['Packs pensados para transformar un espacio completo y ahorrar en cada planta.', 'Packs pensados para transformar um espaço completo e poupar em cada planta.'],
        ['Consigue un 10 % en tu primera compra', 'Receba 10% na sua primeira compra'],
        ['Suscríbete y recibe por email tu descuento de bienvenida. También recibirás novedades y consejos para elegir y cuidar tus plantas.', 'Subscreva e receba por email o desconto de boas-vindas, novidades e conselhos para escolher e cuidar das suas plantas.'],
      ]);
      const hero = exactText(document, 'h1,h2,h3', 'A planta perfeita para a sua casa existe');
      const lower = exactText(document, 'h1,h2,h3', 'Comprar Plantas Online | DECOALIVE: A Sua Loja de Plantas ao Domicílio');
      if (hero) replaceTag(hero, 'h1').classList.add('decoalive-primary-h1');
      if (lower) replaceTag(lower, 'h2');
    }
  }

  function improveCollection() {
    if (!PATH.includes('/collections/')) return;
    const heading = qs('main h1');
    if (!heading) return;
    const cleanHeading = heading.textContent.replace(/^\s*(Colección|Coleção):\s*/i, '').trim();
    if (heading.textContent !== cleanHeading) heading.textContent = cleanHeading;
  }

  function improveDionaea() {
    if (!PATH.endsWith('/products/dionaea-muscipula')) return;
    const productRoot = qs('.product') || document.body;
    const traits = qsa('.plant-trait', productRoot);
    const light = traits.find((element) => /Luz:\s*(Indirecta|Indireta)/i.test(element.textContent));
    const water = traits.find((element) => /Riego:\s*Moderado|Rega:\s*Moderada/i.test(element.textContent));
    if (light?.lastChild) light.lastChild.textContent = IS_PT ? ' Sol direto · muita luz' : ' Sol directo · mucha luz';
    if (water?.lastChild) water.lastChild.textContent = IS_PT ? ' Água destilada · bandeja' : ' Agua destilada · bandeja';
  }

  function improveBlogIndex() {
    if (PATH !== '/blogs/plantas' && PATH !== '/pt/blogs/plantas') return;
    document.body.classList.add('decoalive-blog-index');
    const metaText = IS_PT
      ? 'Guias de plantas de interior, cuidados, decoração e soluções para escolher melhor. Conselhos DECOALIVE e ligação direta às plantas recomendadas.'
      : 'Guías de plantas de interior, cuidados, decoración y soluciones para elegir mejor. Consejos DECOALIVE y acceso directo a las plantas recomendadas.';
    let meta = qs('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = metaText;
    const heading = qs('main h1');
    if (heading && /^(Plantas|Plantas)$/.test(heading.textContent.trim())) {
      heading.textContent = IS_PT ? 'Guias de plantas, cuidados e decoração' : 'Guías de plantas, cuidados y decoración';
    }
    document.title = IS_PT ? 'Guias de plantas e cuidados | DECOALIVE' : 'Guías de plantas y cuidados | DECOALIVE';
    if (heading && !qs('.decoalive-blog-topics')) {
      const nav = document.createElement('nav');
      nav.className = 'decoalive-blog-topics';
      nav.setAttribute('aria-label', IS_PT ? 'Explorar por necessidade' : 'Explorar por necesidad');
      const topics = IS_PT
        ? [
            ['/pt/collections/plantas-resistentes', 'Para principiantes'],
            ['/pt/collections/plantas-que-necesitan-poca-luz', 'Pouca luz'],
            ['/pt/collections/plantas-pet-friendly', 'Casas com animais'],
            ['/pt/collections/plantas-de-interior', 'Comprar plantas'],
          ]
        : [
            ['/collections/plantas-resistentes', 'Para principiantes'],
            ['/collections/plantas-que-necesitan-poca-luz', 'Poca luz'],
            ['/collections/plantas-pet-friendly', 'Hogares con mascotas'],
            ['/collections/plantas-de-interior', 'Comprar plantas'],
          ];
      nav.innerHTML = topics.map(([href, label]) => `<a href="${href}">${label}</a>`).join('');
      heading.insertAdjacentElement('afterend', nav);
    }
  }

  function improveArticle() {
    if (!PATH.includes('/blogs/plantas/')) return;
    document.body.classList.add('decoalive-article');
    const heading = qs('main h1');
    if (heading && !qs('.decoalive-editorial-byline')) {
      const byline = document.createElement('p');
      byline.className = 'decoalive-editorial-byline';
      byline.innerHTML = IS_PT
        ? '<strong>Revisto pela equipa editorial DECOALIVE</strong><span>Conteúdo especializado em seleção e cuidados de plantas.</span>'
        : '<strong>Revisado por el equipo editorial de DECOALIVE</strong><span>Contenido especializado en selección y cuidado de plantas.</span>';
      heading.insertAdjacentElement('afterend', byline);
    }
    const ogImage = qs('meta[property="og:image"]')?.content || qs('main img')?.currentSrc || qs('main img')?.src;
    for (const script of qsa('script[type="application/ld+json"]')) {
      try {
        const data = JSON.parse(script.textContent);
        if (data['@type'] !== 'Article') continue;
        data.author = { '@type': 'Organization', name: 'DECOALIVE', url: 'https://decoalive.com/' };
        data.publisher = { ...(data.publisher || {}), '@type': 'Organization', name: 'DECOALIVE', url: 'https://decoalive.com/' };
        if (ogImage) data.image = [ogImage];
        const updated = JSON.stringify(data);
        if (script.textContent !== updated) script.textContent = updated;
      } catch (_) {}
    }
  }

  function improveProductSchema() {
  if (!PATH.includes('/products/')) return;

  const metaDescription = (qs('meta[name="description"]')?.content || '')
    .replace(/\s+/g, ' ')
    .trim();

  const normalizeDescription = (value) => {
    const text = String(value || metaDescription)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return '';
    if (text.length <= 4800) return text;
    const candidate = text.slice(0, 4800);
    const wordBoundary = candidate.lastIndexOf(' ');
    return `${candidate.slice(0, wordBoundary > 4300 ? wordBoundary : 4800)}…`;
  };

  const normalizeNode = (node) => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(normalizeNode);
      return;
    }

    const types = Array.isArray(node['@type']) ? node['@type'] : [node['@type']];
    if (types.includes('Product') || types.includes('ProductGroup')) {
      const description = normalizeDescription(node.description);
      if (description) node.description = description;
      node.brand = { '@type': 'Brand', name: 'DECOALIVE' };
    }

    Object.values(node).forEach(normalizeNode);
  };

  for (const script of qsa('script[type="application/ld+json"]')) {
    try {
      const data = JSON.parse(script.textContent);
      normalizeNode(data);
      const updated = JSON.stringify(data);
      if (script.textContent !== updated) script.textContent = updated;
    } catch (_) {}
  }
}
  function improveContact() {
    if (PATH !== '/pages/contact' && PATH !== '/pt/pages/contact') return;
    const contact = qs('.contact') || qs('main');
    if (!contact || qs('.decoalive-contact-intro')) return;
    const panel = document.createElement('section');
    panel.className = 'decoalive-contact-intro';
    panel.innerHTML = IS_PT
      ? '<p class="decoalive-eyebrow">ATENDIMENTO DECOALIVE</p><h2>Estamos aqui para ajudar</h2><p>Indique o número da encomenda e explique-nos o que aconteceu. Se a planta chegou danificada, junte fotografias da caixa e da planta para podermos avaliar corretamente.</p><div class="decoalive-contact-grid"><div><strong>Encomenda atrasada</strong><span>Indique o número da encomenda e o código postal.</span></div><div><strong>Planta danificada</strong><span>Junte fotografias da embalagem e da planta.</span></div><div><strong>Dúvidas de cuidados</strong><span>Diga-nos a espécie, luz disponível e como a rega.</span></div></div><a href="mailto:info@decoalive.com">info@decoalive.com</a>'
      : '<p class="decoalive-eyebrow">ATENCIÓN DECOALIVE</p><h2>Estamos aquí para ayudarte</h2><p>Indica tu número de pedido y cuéntanos qué ha ocurrido. Si la planta ha llegado dañada, adjunta fotografías de la caja y de la planta para que podamos valorarlo correctamente.</p><div class="decoalive-contact-grid"><div><strong>Pedido retrasado</strong><span>Indica el número de pedido y el código postal.</span></div><div><strong>Planta dañada</strong><span>Adjunta fotografías del embalaje y de la planta.</span></div><div><strong>Dudas de cuidados</strong><span>Cuéntanos la especie, la luz disponible y cómo la riegas.</span></div></div><a href="mailto:info@decoalive.com">info@decoalive.com</a>';
    contact.prepend(panel);
  }

  function improveCart() {
    if (PATH !== '/cart' && PATH !== '/pt/cart') return;
    const footer = qs('.cart__footer') || qs('#main-cart-footer');
    if (!footer || qs('.decoalive-cart-confidence')) return;
    const strip = document.createElement('aside');
    strip.className = 'decoalive-cart-confidence';
    strip.innerHTML = IS_PT
      ? '<div><strong>Pagamento seguro</strong><span>Transação protegida</span></div><div><strong>Envio peninsular grátis</strong><span>Entrega prevista em 24–48 h</span></div><div><strong>Devolução grátis</strong><span>Compra com tranquilidade</span></div><a href="mailto:info@decoalive.com"><strong>Precisa de ajuda?</strong><span>info@decoalive.com</span></a>'
      : '<div><strong>Pago seguro</strong><span>Transacción protegida</span></div><div><strong>Envío peninsular gratis</strong><span>Entrega prevista en 24–48 h</span></div><div><strong>Devolución gratuita</strong><span>Compra con tranquilidad</span></div><a href="mailto:info@decoalive.com"><strong>¿Necesitas ayuda?</strong><span>info@decoalive.com</span></a>';
    footer.prepend(strip);
  }

  function localizeReviews() {
    replaceVisibleText(document.body, [
      ['Sort reviews by', IS_PT ? 'Ordenar avaliações por' : 'Ordenar reseñas por'],
      ['Thumbnail Navigation', IS_PT ? 'Navegação de miniaturas' : 'Navegación de miniaturas'],
    ]);
  }

  function run() {
    ensureNavigationLinks();
    improveHome();
    improveCollection();
    improveDionaea();
    improveBlogIndex();
    improveArticle();
    improveProductSchema();
    improveContact();
    improveCart();
    localizeReviews();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
  let observerTimer;
  new MutationObserver(() => {
    window.clearTimeout(observerTimer);
    observerTimer = window.setTimeout(run, 180);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* DECOALIVE trust consistency — 2026-09-01 */
(() => {
  'use strict';

  const PATH = window.location.pathname.replace(/\/$/, '') || '/';
  const IS_PT = PATH === '/pt' || PATH.startsWith('/pt/');
  let timer = 0;

  function syncHomeProof() {
    if (PATH !== '/' && PATH !== '/pt') return;
    const source = document.querySelector('.jdgm-rating-text');
    const countSource = document.querySelector('.jdgm-reviews-count');
    const proof = document.querySelector('.decoalive-proof__rating');
    if (!source || !countSource || !proof) return;

    const ratingMatch = source.textContent.match(/(\d+[.,]\d+)/);
    const count = Number((countSource.textContent || '').replace(/\D/g, ''));
    if (!ratingMatch || !count) return;

    const rating = ratingMatch[1].replace('.', ',');
    let ratingTarget = proof.querySelector('strong');
    if (!ratingTarget) {
      ratingTarget = document.createElement('strong');
      ratingTarget.className = 'cro-home-rating';
      const stars = proof.querySelector('.decoalive-proof__stars');
      if (stars) stars.insertAdjacentElement('afterend', ratingTarget);
      else proof.prepend(ratingTarget);
    }

    const countTarget = proof.querySelector('small') || [...proof.querySelectorAll('span, p')].find((el) =>
      !el.querySelector('strong,small') && /opiniones verificadas|opiniões verificadas/i.test(el.textContent)
    );

    if (ratingTarget.textContent.trim() !== rating + '/5') {
      ratingTarget.textContent = rating + '/5';
    }
    if (countTarget) {
      const separator = IS_PT ? '\u00a0' : '.';
      const formatted = String(count).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      const copy = IS_PT ? 'opiniões verificadas' : 'opiniones verificadas';
      const next = formatted + ' ' + copy;
      if (countTarget.textContent.trim() !== next) countTarget.textContent = next;
      proof.setAttribute('aria-label', rating + '/5, ' + next);
    }
  }

  function syncProductProof() {
    if (!PATH.includes('/products/')) return;
    const summary = document.querySelector('.jm-average-rating-display');
    const badge = document.querySelector('.jdgm-prev-badge');
    const badgeText = document.querySelector('.jdgm-prev-badge__text');
    if (!summary || !badge || !badgeText) return;

    const texts = [...summary.querySelectorAll('.jm-text')].map((el) => el.textContent.trim());
    const rating = texts.find((text) => /^\d+[.,]\d+$/.test(text));
    const countText = texts.find((text) => /\d+\s+(reseñas|avaliações)/i.test(text));
    const countMatch = countText && countText.match(/\d+/);
    if (!rating || !countMatch) return;

    const count = Number(countMatch[0]);
    const localizedRating = rating.replace('.', ',');
    const noun = IS_PT ? (count === 1 ? 'avaliação verificada' : 'avaliações verificadas') : (count === 1 ? 'reseña verificada' : 'reseñas verificadas');
    const next = localizedRating + '/5 · ' + count + ' ' + noun;

    if (badgeText.textContent.trim() !== next) badgeText.textContent = next;
    badgeText.classList.add('cro-rating-synced');
    badge.setAttribute('data-average-rating', rating.replace(',', '.'));
    badge.setAttribute('data-number-of-reviews', String(count));
    badge.setAttribute('aria-label', next);
  }

  function run() {
    syncHomeProof();
    syncProductProof();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();

  [800, 1800, 3500, 6500].forEach((delay) => window.setTimeout(run, delay));
  let trustPasses = 0;
  const trustTimer = window.setInterval(() => {
    run();
    trustPasses += 1;
    if (trustPasses >= 30) window.clearInterval(trustTimer);
  }, 1000);
  new MutationObserver(() => {
    window.clearTimeout(timer);
    timer = window.setTimeout(run, 160);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();


/* DECOALIVE conversion layer — 2026-08-31 */
(() => {
  'use strict';

  const PATH = window.location.pathname.replace(/\/$/, '') || '/';
  const IS_PT = PATH === '/pt' || PATH.startsWith('/pt/');
  const ROOT = window.Shopify?.routes?.root || (IS_PT ? '/pt/' : '/');
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const money = (cents) => new Intl.NumberFormat(IS_PT ? 'pt-PT' : 'es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number(cents || 0) / 100);

  let reviewSortApplied = false;
  let scheduled = 0;

  function syncCartCount(cart) {
    if (!cart) return;
    qsa('.cart-count-bubble span[aria-hidden="true"], .cart-count-bubble span:first-child').forEach((element) => {
      element.textContent = String(cart.item_count || 0);
    });
  }

  async function addItems(items, button) {
    const idleText = button.textContent;
    button.disabled = true;
    button.textContent = IS_PT ? 'A adicionar…' : 'Añadiendo…';
    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('Cart request failed');
      const cart = await fetch(`${ROOT}cart.js`).then((result) => result.json());
      syncCartCount(cart);
      button.textContent = IS_PT ? 'Adicionado ✓' : 'Añadido ✓';
      const wrap = button.closest('.cro-match-purchase, .cro-match-bundle');
      if (wrap && !wrap.querySelector('.cro-match-cart-link')) {
        const link = document.createElement('a');
        link.className = 'cro-match-cart-link';
        link.href = `${ROOT}cart`;
        link.textContent = IS_PT ? 'Ver carrinho →' : 'Ver carrito →';
        wrap.appendChild(link);
      }
    } catch (_) {
      button.disabled = false;
      button.textContent = IS_PT ? 'Tentar novamente' : 'Intentar de nuevo';
      window.setTimeout(() => { button.textContent = idleText; }, 2400);
    }
  }

  function createVariantSelect(product, variants) {
    const select = document.createElement('select');
    select.className = 'cro-match-variant';
    select.setAttribute('aria-label', IS_PT ? 'Escolher tamanho' : 'Elegir tamaño');
    variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.dataset.price = String(variant.price);
      option.textContent = `${variant.title === 'Default Title' ? (IS_PT ? 'Tamanho único' : 'Tamaño único') : variant.title} · ${money(variant.price)}`;
      select.appendChild(option);
    });
    return select;
  }

  async function enrichPlantMatchCard(card) {
    if (card.dataset.croCommerce === 'ready' || card.dataset.croCommerce === 'loading') return;
    const handle = card.dataset.resultId;
    if (!handle) return;
    card.dataset.croCommerce = 'loading';
    try {
      const product = await fetch(`${ROOT}products/${handle}.js`).then((response) => {
        if (!response.ok) throw new Error('Product request failed');
        return response.json();
      });
      const variants = (product.variants || []).filter((variant) => variant.available);
      const body = card.querySelector('.pf-card__body');
      const currentCta = body?.querySelector('.pf-card__cta');
      if (!body || !currentCta) throw new Error('Card structure unavailable');

      const purchase = document.createElement('div');
      purchase.className = 'cro-match-purchase';

      const meta = document.createElement('div');
      meta.className = 'cro-match-purchase__meta';
      const price = document.createElement('strong');
      price.textContent = variants.length ? `${IS_PT ? 'Desde' : 'Desde'} ${money(Math.min(...variants.map((variant) => variant.price)))}` : (IS_PT ? 'Indisponível' : 'No disponible');
      const stock = document.createElement('span');
      stock.textContent = variants.length ? (IS_PT ? 'Disponível agora' : 'Disponible ahora') : (IS_PT ? 'Esgotado' : 'Agotado');
      meta.append(price, stock);
      purchase.appendChild(meta);

      if (variants.length) {
        const select = createVariantSelect(product, variants);
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'cro-match-add';
        add.textContent = IS_PT ? 'Adicionar ao carrinho' : 'Añadir al carrito';
        add.addEventListener('click', () => addItems([{ id: Number(select.value), quantity: 1 }], add));
        purchase.append(select, add);
      }

      currentCta.textContent = IS_PT ? 'Ver detalhes e cuidados →' : 'Ver detalles y cuidados →';
      currentCta.classList.remove('pf-button--primary');
      currentCta.classList.add('pf-button--secondary');
      body.insertBefore(purchase, currentCta);
      card.dataset.croCommerce = 'ready';
    } catch (_) {
      card.dataset.croCommerce = 'error';
    }
  }

  async function updatePlantMatchBundle() {
    if (!PATH.endsWith('/pages/plant-match')) return;
    const results = document.querySelector('.pf-results');
    const grid = results?.querySelector('.pf-results__grid');
    const cards = grid ? qsa('.pf-card[data-cro-commerce="ready"]', grid) : [];
    if (!results || !grid || cards.length < 2 || results.querySelector('.cro-match-bundle')) return;

    const bundle = document.createElement('div');
    bundle.className = 'cro-match-bundle';
    const copy = document.createElement('div');
    copy.innerHTML = IS_PT
      ? '<strong>Leve a seleção completa</strong><span>Adicione as plantas recomendadas numa só ação.</span>'
      : '<strong>Llévate la selección completa</strong><span>Añade las plantas recomendadas en una sola acción.</span>';
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = IS_PT ? `Adicionar ${cards.length} plantas` : `Añadir ${cards.length} plantas`;
    button.addEventListener('click', () => {
      const items = cards.map((card) => ({
        id: Number(card.querySelector('.cro-match-variant')?.value),
        quantity: 1,
      })).filter((item) => item.id);
      if (items.length) addItems(items, button);
    });
    bundle.append(copy, button);
    grid.insertAdjacentElement('afterend', bundle);
  }

  function improvePlantMatch() {
    if (!PATH.endsWith('/pages/plant-match')) return;
    const cards = qsa('.pf-results .pf-card');
    if (!cards.length) return;
    Promise.all(cards.map((card) => enrichPlantMatchCard(card))).then(() => updatePlantMatchBundle());
  }

  function improveCart() {
    if (!PATH.endsWith('/cart')) return;
    const checkout = document.querySelector('#checkout');
    const total = document.querySelector('.totals__total-value');
    const hasItems = Boolean(document.querySelector('.cart-item')) && checkout && !checkout.disabled;
    if (!checkout || !total || !hasItems) {
      document.querySelector('.cro-cart-mobile-bar')?.remove();
      document.body.classList.remove('cro-cart-mobile-ready');
      return;
    }
    document.body.classList.add('cro-cart-mobile-ready');
    let bar = document.querySelector('.cro-cart-mobile-bar');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'cro-cart-mobile-bar';
      bar.innerHTML = `<div><span>${IS_PT ? 'Total com envio incluído' : 'Total con envío incluido'}</span><strong></strong></div>`;
      const button = document.createElement('button');
      button.type = 'submit';
      button.name = 'checkout';
      button.setAttribute('form', 'cart');
      button.textContent = IS_PT ? 'Finalizar compra' : 'Pagar pedido';
      bar.appendChild(button);
      document.body.appendChild(bar);
    }
    const stickyTotal = bar.querySelector('strong');
    if (stickyTotal) stickyTotal.textContent = total.textContent.trim();
  }

  function correctCollectionCare() {
    qsa('.card-wrapper').forEach((card) => {
      if (!card.querySelector('a[href*="/products/dionaea-muscipula"]')) return;
      const traits = qsa('.decoalive-card-care span', card);
      if (traits[0]) traits[0].textContent = IS_PT ? '☀️ Sol direto' : '☀️ Sol directo';
      if (traits[1]) traits[1].textContent = IS_PT ? '💧 Água destilada' : '💧 Agua destilada';
    });
  }

  async function correctFlowerArticleOffer() {
    if (!PATH.endsWith('/blogs/plantas/plantas-de-interior-con-flor')) return;
    const offer = document.querySelector('.article-product-cta');
    if (!offer || offer.dataset.croCorrected) return;
    offer.dataset.croCorrected = 'loading';
    try {
      const product = await fetch(`${ROOT}products/spathiphyllum.js`).then((response) => response.json());
      qsa('a', offer).forEach((link) => { link.href = `${ROOT}products/spathiphyllum`; });
      const image = offer.querySelector('img');
      if (image) {
        image.src = product.featured_image || product.images?.[0] || image.src;
        image.removeAttribute('srcset');
        image.alt = product.title;
      }
      const imageLink = offer.querySelector('.article-product-cta__image');
      if (imageLink) imageLink.setAttribute('aria-label', product.title);
      const title = offer.querySelector('.article-product-cta__title');
      if (title) title.textContent = product.title;
      const price = offer.querySelector('.article-product-cta__price');
      if (price) price.textContent = money(product.price);
      const cta = offer.querySelector('.article-product-cta__buy .button');
      if (cta) cta.textContent = IS_PT ? 'Ver Spathiphyllum' : 'Ver Spathiphyllum';
      offer.dataset.croCorrected = 'ready';
    } catch (_) {
      offer.dataset.croCorrected = 'error';
    }
  }

  function improveReviews() {
    const widget = document.querySelector('.jdgm-widget-revamp');
    if (!widget) return;
    qsa('label.jdgm-visually-hidden', widget).forEach((label) => {
      if (label.textContent.trim() === 'Sort reviews by') label.textContent = IS_PT ? 'Ordenar avaliações por' : 'Ordenar reseñas por';
    });
    qsa('.sr-only', widget).forEach((element) => {
      if (element.textContent.trim() === 'Thumbnail Navigation') element.textContent = IS_PT ? 'Navegação de miniaturas' : 'Navegación de miniaturas';
    });

    if (!document.querySelector('.cro-review-intro')) {
      const intro = document.createElement('div');
      intro.className = 'cro-review-intro';
      intro.innerHTML = IS_PT
        ? '<strong>Opiniões reais de compras verificadas</strong><span>Experiências positivas e incidências ajudam a comprar com mais informação.</span>'
        : '<strong>Opiniones reales de compras verificadas</strong><span>Las experiencias positivas y las incidencias te ayudan a comprar con más información.</span>';
      widget.insertAdjacentElement('beforebegin', intro);
    }

    const sort = widget.querySelector('select.jdgm-sort-dropdown');
    if (sort && !reviewSortApplied && qsa('option', sort).some((option) => option.value === 'most_helpful')) {
      reviewSortApplied = true;
      sort.value = 'most_helpful';
      sort.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function run() {
    improvePlantMatch();
    improveCart();
    correctCollectionCare();
    correctFlowerArticleOffer();
    improveReviews();
  }

  const observer = new MutationObserver(() => {
    window.clearTimeout(scheduled);
    scheduled = window.setTimeout(run, 140);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      run();
      observer.observe(document.body, { childList: true, subtree: true });
    }, { once: true });
  } else {
    run();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  let reviewPasses = 0;
  const reviewTimer = window.setInterval(() => {
    improveReviews();
    reviewPasses += 1;
    if (reviewPasses >= 15) window.clearInterval(reviewTimer);
  }, 1000);
})();



/* DECOALIVE PREMIUM UX 2026-09-01
   Isolated, idempotent conversion improvements. */
(function () {
  'use strict';

  if (window.__decoalivePremiumUx20260901) return;
  window.__decoalivePremiumUx20260901 = true;

  var MOBILE_QUERY = '(max-width: 749px)';
  var reviewTimer = null;
  var assistantFrame = 0;

  function isVisible(element) {
    if (!element) return false;
    var rect = element.getBoundingClientRect();
    var style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  }

  function intersects(a, b, padding) {
    padding = padding || 0;
    return !(
      a.right + padding < b.left ||
      a.left - padding > b.right ||
      a.bottom + padding < b.top ||
      a.top - padding > b.bottom
    );
  }

  function getPurchaseWrapper(info) {
    var form = info && info.querySelector('product-form.product-form');
    if (!form) return null;
    var wrapper = form.parentElement;
    return wrapper && wrapper.parentElement === info ? wrapper : form;
  }

  function reorderProductInformation() {
    document.querySelectorAll('[id^="ProductInfo-"]').forEach(function (info) {
      if (info.dataset.decoPremiumOrderReady === 'true') return;
      var receive = info.querySelector('.deco-que-recibiras');
      var purchaseWrapper = getPurchaseWrapper(info);
      if (!receive || !purchaseWrapper) return;
      purchaseWrapper.insertAdjacentElement('afterend', receive);
      info.dataset.decoPremiumOrderReady = 'true';
    });
  }

  function featuredGalleryMedia() {
    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      if (gallery.dataset.decoPremiumFeaturedReady === 'true') return;
      var firstThumbnail = gallery.querySelector('[id^="GalleryThumbnails"] [data-target]');
      var target = firstThumbnail && firstThumbnail.dataset.target;
      if (!target || typeof gallery.setActiveMedia !== 'function') return;
      var active = gallery.querySelector('[id^="GalleryViewer"] .is-active[data-media-id]');
      if (!active || active.dataset.mediaId !== target) {
        gallery.setActiveMedia(target, true);
      }
      gallery.dataset.decoPremiumFeaturedReady = 'true';
    });
  }

  function localAddLabel() {
    return document.documentElement.lang.toLowerCase().indexOf('pt') === 0
      ? 'Adicionar ao carrinho'
      : 'Añadir al carrito';
  }

  function syncMobileBuybar() {
    var mobile = window.matchMedia(MOBILE_QUERY).matches;
    document.querySelectorAll('.deco-mobile-buybar').forEach(function (bar) {
      var section = bar.closest('[id^="MainProduct-"]') || document;
      var nativeButton = section.querySelector('.deco-mobile-buybar__native-submit');
      var stickyButton = bar.querySelector('button');
      var price = bar.querySelector('.deco-mobile-buybar__price');

      if (stickyButton) {
        var label = localAddLabel() + (price && price.textContent.trim() ? ' · ' + price.textContent.trim() : '');
        if (stickyButton.textContent.trim() !== label) stickyButton.textContent = label;
        if (bar.getAttribute('aria-label') !== localAddLabel()) bar.setAttribute('aria-label', localAddLabel());
      }

      if (!mobile || !nativeButton) {
        bar.hidden = true;
        bar.style.setProperty('display', 'none', 'important');
        return;
      }

      var rect = nativeButton.getBoundingClientRect();
      // Show only after the shopper has passed the native purchase button.
      var shouldShow = rect.bottom < 0;
      bar.hidden = !shouldShow;
      bar.style.setProperty('display', shouldShow ? 'grid' : 'none', 'important');
      document.documentElement.classList.toggle('deco-mobile-buybar-visible', shouldShow);
      document.body.classList.toggle('deco-mobile-buybar-visible', shouldShow);
    });
  }

  function protectAssistant() {
    assistantFrame = 0;
    var root = document.getElementById('decoalive-assistant-root');
    var launcher = root && root.querySelector('.decoalive-assistant-launcher');
    if (!root || !launcher) return;

    if (!window.matchMedia(MOBILE_QUERY).matches) {
      root.style.removeProperty('bottom');
      return;
    }

    var panelOpen = launcher.getAttribute('aria-expanded') === 'true';
    if (panelOpen) return;

    var clearance = 16;
    document.querySelectorAll('.deco-mobile-buybar, .cro-cart-mobile-bar').forEach(function (bar) {
      if (isVisible(bar)) clearance = Math.max(clearance, window.innerHeight - bar.getBoundingClientRect().top + 16);
    });
    var bottom = 'max(' + Math.ceil(clearance) + 'px, env(safe-area-inset-bottom))';
    if (root.style.getPropertyValue('bottom') !== bottom) root.style.setProperty('bottom', bottom, 'important');
  }

  function scheduleAssistantProtection() {
    if (assistantFrame) return;
    assistantFrame = window.requestAnimationFrame(protectAssistant);
  }

  function normalizeReviewDates(root) {
    if (!root) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      var value = node.nodeValue && node.nodeValue.trim();
      var match = value && value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) continue;
      var month = Number(match[1]);
      var day = Number(match[2]);
      if (month < 1 || month > 12 || day < 1 || day > 31) continue;
      node.nodeValue = match[2] + '/' + match[1] + '/' + match[3];
    }
  }

  function preferRecentReviews(root) {
    if (!root) return;
    var selects = root.querySelectorAll('select');
    selects.forEach(function (select) {
      var recent = Array.prototype.find.call(select.options, function (option) {
        return /más recientes|most recent|mais recentes/i.test(option.textContent || '');
      });
      if (recent && select.value !== recent.value && select.dataset.decoPremiumSorted !== 'true') {
        select.value = recent.value;
        select.dataset.decoPremiumSorted = 'true';
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function normalizeReviews() {
    document.querySelectorAll('#judgeme_product_reviews, .jdgm-widget').forEach(function (widget) {
      normalizeReviewDates(widget);
      preferRecentReviews(widget);
      widget.querySelectorAll('*').forEach(function (element) {
        if (!element.shadowRoot) return;
        normalizeReviewDates(element.shadowRoot);
        preferRecentReviews(element.shadowRoot);
      });
    });
  }

  function initialize() {
    reorderProductInformation();
    featuredGalleryMedia();
    syncMobileBuybar();
    scheduleAssistantProtection();
    normalizeReviews();

    window.clearTimeout(reviewTimer);
    reviewTimer = window.setTimeout(normalizeReviews, 1600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  window.addEventListener('scroll', function () {
    syncMobileBuybar();
    scheduleAssistantProtection();
  }, { passive: true });
  window.addEventListener('resize', function () {
    syncMobileBuybar();
    scheduleAssistantProtection();
  });
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('variant:change', initialize);

  var pendingPremiumFrame = 0;
  var observer = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (mutation) {
      return Array.prototype.some.call(mutation.addedNodes, function (node) { return node.nodeType === 1; });
    });
    if (!relevant || pendingPremiumFrame) return;
    pendingPremiumFrame = window.requestAnimationFrame(function () {
      pendingPremiumFrame = 0;
      reorderProductInformation();
      featuredGalleryMedia();
      syncMobileBuybar();
      scheduleAssistantProtection();
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();



;(() => {
  'use strict';

  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  const isPt = pathname === '/pt' || pathname.startsWith('/pt/');
  const route = pathname.replace(/^\/pt(?=\/|$)/, '') || '/';
  const prefix = isPt ? '/pt' : '';

  function filteredHref(base, filters, sortBy) {
    const params = new URLSearchParams();
    params.set('filter.v.availability', '1');
    filters.forEach(([name, value]) => params.set(name, value));
    params.set('sort_by', sortBy || 'best-selling');
    return prefix + base + '?' + params.toString();
  }

  function collectionGuideConfig() {
    const interior = isPt
      ? {
          eyebrow: 'ESCOLHA PELO QUE PRECISA',
          title: 'Encontre a sua planta ideal',
          description: 'Escolha segundo a sua casa, a luz e a sua rotina.',
          items: [
            ['🐾', 'Pet-friendly', filteredHref('/collections/plantas-pet-friendly', [])],
            ['🌱', 'Para iniciantes', filteredHref('/collections/plantas-resistentes', [])],
            ['🌙', 'Pouca luz', filteredHref('/collections/plantas-que-necesitan-poca-luz', [])],
            ['💧', 'Pouca água', filteredHref('/collections/plantas-que-necesitan-poca-agua', [])],
            ['🌴', 'Plantas grandes', filteredHref('/collections/plantas-grandes-de-interior', [])],
            ['🎁', 'Packs poupança', filteredHref('/collections/packs-plantas-interior', [])],
          ],
        }
      : {
          eyebrow: 'ELIGE POR LO QUE NECESITAS',
          title: 'Encuentra tu planta ideal',
          description: 'Elige según tu hogar, la luz y tu ritmo de vida.',
          items: [
            ['🐾', 'Pet-friendly', filteredHref('/collections/plantas-pet-friendly', [])],
            ['🌱', 'Para principiantes', filteredHref('/collections/plantas-resistentes', [])],
            ['🌙', 'Poca luz', filteredHref('/collections/plantas-que-necesitan-poca-luz', [])],
            ['💧', 'Poca agua', filteredHref('/collections/plantas-que-necesitan-poca-agua', [])],
            ['🌴', 'Plantas grandes', filteredHref('/collections/plantas-grandes-de-interior', [])],
            ['🎁', 'Packs ahorro', filteredHref('/collections/packs-plantas-interior', [])],
          ],
        };

    const kokedamas = isPt
      ? {
          eyebrow: 'ENCONTRE O SEU ESTILO',
          title: 'Encontre a sua kokedama',
          description: 'Escolha entre as opções disponíveis por luz ou orçamento.',
          items: [
            ['🌿', 'Todas disponíveis', filteredHref('/collections/kokedamas', [])],
            ['🌤️', 'Meia-sombra', filteredHref('/collections/kokedamas', [['filter.v.t.shopify.sunlight', 'gid://shopify/TaxonomyValue/8577']])],
            ['🎁', 'Até 40 €', filteredHref('/collections/kokedamas', [['filter.v.price.lte', '40']])],
            ['✨', 'De 40 a 50 €', filteredHref('/collections/kokedamas', [['filter.v.price.gte', '40.01'], ['filter.v.price.lte', '50']])],
            ['🆕', 'Novidades', filteredHref('/collections/kokedamas', [], 'created-descending')],
          ],
        }
      : {
          eyebrow: 'ENCUENTRA TU ESTILO',
          title: 'Encuentra tu kokedama',
          description: 'Elige entre las opciones disponibles por luz o presupuesto.',
          items: [
            ['🌿', 'Todas disponibles', filteredHref('/collections/kokedamas', [])],
            ['🌤️', 'Sombra parcial', filteredHref('/collections/kokedamas', [['filter.v.t.shopify.sunlight', 'gid://shopify/TaxonomyValue/8577']])],
            ['🎁', 'Hasta 40 €', filteredHref('/collections/kokedamas', [['filter.v.price.lte', '40']])],
            ['✨', 'De 40 a 50 €', filteredHref('/collections/kokedamas', [['filter.v.price.gte', '40.01'], ['filter.v.price.lte', '50']])],
            ['🆕', 'Novedades', filteredHref('/collections/kokedamas', [], 'created-descending')],
          ],
        };

    const exterior = isPt
      ? {
          eyebrow: 'ESCOLHA PARA O SEU ESPAÇO',
          title: 'Encontre plantas para o terraço ou jardim',
          description: 'Apenas opções disponíveis, organizadas por luz e orçamento.',
          items: [
            ['🌿', 'Todas disponíveis', filteredHref('/collections/plantas-de-exterior', [])],
            ['☀️', 'Varandas soalheiras', filteredHref('/collections/plantas-de-exterior', [['filter.v.t.shopify.sunlight', 'gid://shopify/TaxonomyValue/8576']])],
            ['🌤️', 'Terraços com meia-sombra', filteredHref('/collections/plantas-de-exterior', [['filter.v.t.shopify.sunlight', 'gid://shopify/TaxonomyValue/8577']])],
            ['🏷️', 'Até 30 €', filteredHref('/collections/plantas-de-exterior', [['filter.v.price.lte', '30']])],
          ],
        }
      : {
          eyebrow: 'ELIGE PARA TU ESPACIO',
          title: 'Encuentra plantas para tu terraza o jardín',
          description: 'Solo opciones disponibles, organizadas por luz y presupuesto.',
          items: [
            ['🌿', 'Todas disponibles', filteredHref('/collections/plantas-de-exterior', [])],
            ['☀️', 'Balcones soleados', filteredHref('/collections/plantas-de-exterior', [['filter.v.t.shopify.sunlight', 'gid://shopify/TaxonomyValue/8576']])],
            ['🌤️', 'Terrazas con semisombra', filteredHref('/collections/plantas-de-exterior', [['filter.v.t.shopify.sunlight', 'gid://shopify/TaxonomyValue/8577']])],
            ['🏷️', 'Hasta 30 €', filteredHref('/collections/plantas-de-exterior', [['filter.v.price.lte', '30']])],
          ],
        };

    return {
      '/collections/plantas-de-interior': interior,
      '/collections/kokedamas': kokedamas,
      '/collections/plantas-de-exterior': exterior,
    }[route];
  }

  function ensureGuideStyles() {
    if (document.getElementById('decoalive-category-guide-styles')) return;
    const style = document.createElement('style');
    style.id = 'decoalive-category-guide-styles';
    style.textContent = [
      '.decoalive-category-guide{margin:0 auto 2.4rem;padding-top:.2rem;}',
      '.decoalive-category-guide__panel{background:linear-gradient(135deg,#f7faf6 0%,#f1f6f1 100%);border:1px solid rgba(33,76,52,.14);border-radius:1.8rem;padding:1.8rem 2rem 1.9rem;box-shadow:0 12px 30px rgba(31,62,43,.06);}',
      '.decoalive-category-guide__eyebrow{margin:0 0 .35rem;color:#47705a;font-size:1.05rem;font-weight:700;letter-spacing:.13em;line-height:1.3;}',
      '.decoalive-category-guide__heading{margin:0;color:#183629;font-family:var(--font-heading-family);font-size:clamp(1.8rem,2vw,2.35rem);font-weight:600;letter-spacing:-.02em;line-height:1.2;}',
      '.decoalive-category-guide__description{margin:.45rem 0 1.25rem;color:#557064;font-size:1.35rem;line-height:1.45;}',
      '.decoalive-category-guide__nav{display:flex;gap:.8rem;overflow-x:auto;padding:.15rem .15rem .45rem;scroll-snap-type:x proximity;scrollbar-width:thin;scrollbar-color:rgba(38,89,60,.3) transparent;}',
      '.decoalive-category-chip{display:inline-flex;flex:0 0 auto;align-items:center;gap:.7rem;min-height:4.4rem;padding:.7rem 1.25rem;border:1px solid rgba(35,79,53,.16);border-radius:999px;background:#fff;color:#213e2e;text-decoration:none;font-size:1.38rem;font-weight:600;line-height:1.2;box-shadow:0 4px 14px rgba(31,62,43,.055);scroll-snap-align:start;transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease,background .18s ease;}',
      '.decoalive-category-chip__icon{display:grid;place-items:center;width:2.6rem;height:2.6rem;border-radius:50%;background:#edf5ee;font-size:1.45rem;line-height:1;}',
      '.decoalive-category-chip:hover,.decoalive-category-chip:focus-visible{transform:translateY(-2px);border-color:rgba(31,103,65,.38);background:#fbfdfb;box-shadow:0 9px 20px rgba(31,62,43,.11);outline:none;}',
      '.decoalive-category-chip[aria-current="page"]{border-color:#275f3e;background:#275f3e;color:#fff;box-shadow:0 8px 18px rgba(39,95,62,.2);}',
      '.decoalive-category-chip[aria-current="page"] .decoalive-category-chip__icon{background:rgba(255,255,255,.15);}',
      '@media(max-width:749px){.decoalive-category-guide{margin-bottom:1.8rem;padding-left:1.5rem;padding-right:1.5rem;}.decoalive-category-guide__panel{border-radius:1.5rem;padding:1.45rem 1.3rem 1.35rem;}.decoalive-category-guide__description{font-size:1.27rem;margin-bottom:1rem;}.decoalive-category-guide__nav{margin-right:-1.3rem;padding-right:1.3rem;}.decoalive-category-chip{min-height:4.2rem;padding:.65rem 1.05rem;font-size:1.3rem;}}',
    ].join('');
    document.head.appendChild(style);
  }

  function mountCollectionGuide() {
    const config = collectionGuideConfig();
    if (!config) return;

    const main = document.querySelector('main');
    const h1 = main && main.querySelector('h1');
    if (!main || !h1) return;

    const legacyLink = main.querySelector('a[href*="plantas-pet-friendly"]');
    const legacyNav = legacyLink && legacyLink.closest('nav');
    if (legacyNav && !legacyNav.classList.contains('decoalive-category-guide__nav')) legacyNav.remove();
    if (main.querySelector('.decoalive-category-guide')) return;

    ensureGuideStyles();

    const section = document.createElement('section');
    section.className = 'decoalive-category-guide page-width';
    section.setAttribute('aria-labelledby', 'decoalive-category-guide-title');

    const panel = document.createElement('div');
    panel.className = 'decoalive-category-guide__panel';

    const eyebrow = document.createElement('p');
    eyebrow.className = 'decoalive-category-guide__eyebrow';
    eyebrow.textContent = config.eyebrow;

    const title = document.createElement('h2');
    title.className = 'decoalive-category-guide__heading';
    title.id = 'decoalive-category-guide-title';
    title.textContent = config.title;

    const description = document.createElement('p');
    description.className = 'decoalive-category-guide__description';
    description.textContent = config.description;

    const nav = document.createElement('nav');
    nav.className = 'decoalive-category-guide__nav';
    nav.setAttribute('aria-label', isPt ? 'Filtros rápidos da categoria' : 'Filtros rápidos de la categoría');

    const current = window.location.pathname + window.location.search;
    config.items.forEach(([icon, label, href]) => {
      const link = document.createElement('a');
      link.className = 'decoalive-category-chip';
      link.href = href;
      if (decodeURI(href) === decodeURI(current)) link.setAttribute('aria-current', 'page');

      const iconElement = document.createElement('span');
      iconElement.className = 'decoalive-category-chip__icon';
      iconElement.setAttribute('aria-hidden', 'true');
      iconElement.textContent = icon;

      const labelElement = document.createElement('span');
      labelElement.textContent = label;

      link.append(iconElement, labelElement);
      nav.appendChild(link);
    });

    panel.append(eyebrow, title, description, nav);
    section.appendChild(panel);

    const hero = h1.closest('.collection-hero');
    (hero || h1).insertAdjacentElement('afterend', section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountCollectionGuide, { once: true });
  } else {
    mountCollectionGuide();
  }
  document.addEventListener('shopify:section:load', mountCollectionGuide);
})();
